import { useSelector } from 'modules/react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import { useToggle, useUnmount } from 'react-use';
import {
    useGetNetSuiteRequestsDuplicates,
    useGetQBooksRequestsDuplicates,
    useGetXeroRequestsDuplicates,
} from 'shared/data/webApp/v2';

import { getRequest } from '../../selectors/pageSelectors';
import { CheckDuplicatesPopupProps } from './CheckDuplicatesPopup.types';

export const useDuplicatesPopup = (
    props: CheckDuplicatesPopupProps &
        ReturnType<
            | typeof useGetQBooksRequestsDuplicates
            | typeof useGetXeroRequestsDuplicates
            | typeof useGetNetSuiteRequestsDuplicates
        >
) => {
    const { onSubmit, onCancel, isLoading, error, data, remove } = props;

    useUnmount(remove);

    const [isOpen, toggleIsOpen] = useToggle(false);
    const request = useSelector(getRequest);
    const allDuplicates = useMemo(() => data?.items || [], [data?.items]);
    const currentDuplicates = useMemo(
        () => allDuplicates.filter(({ requestId }) => requestId !== request.id),
        [allDuplicates, request.id]
    );

    /**
     * Close popup on cancel duplicates check
     */
    const handleCancel = useCallback(() => {
        toggleIsOpen(false);
        onCancel();
    }, [onCancel, toggleIsOpen]);

    /**
     * Submit request and cancel duplicates check
     */
    const handleSubmit = useCallback(() => {
        onSubmit(currentDuplicates);
        handleCancel();
    }, [currentDuplicates, handleCancel, onSubmit]);

    /**
     * If there are duplicates for the current request - show popup else - submit request
     */
    useEffect(() => {
        if (isLoading) return;

        if (currentDuplicates.length) {
            toggleIsOpen(true);
        } else {
            handleSubmit();
        }
    }, [allDuplicates.length, currentDuplicates.length, error, handleSubmit, isLoading, toggleIsOpen]);

    return {
        isOpen,
        toggleIsOpen,
        currentDuplicates,
        handleSubmit,
        handleCancel,
    };
};
